import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatUAH } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RotateCcw, Trash2, Search, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function DeletedOperations() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState({});
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dayFilter, setDayFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [u, all, emps] = await Promise.all([
        api.get("/auth/me"),
        api.get("/operations?include_deleted=true"),
        api.get("/employees"),
      ]);
      setMe(u.data);
      const deleted = (all.data || []).filter((o) => o.deleted === true);
      // Sort by deletion date desc
      deleted.sort((a, b) => (b.deleted_at || "").localeCompare(a.deleted_at || ""));
      setItems(deleted);
      // Build user map for "deleted_by" display
      const map = {};
      (emps.data || []).forEach((e) => { if (e.user_id) map[e.user_id] = e.full_name; });
      setUsers(map);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const uniqueDayDates = useMemo(() => {
    const set = new Set(items.map((o) => o.operation_day_date || o.date || "").filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((o) => {
      if (q) {
        const hay = [o.patient_name, o.patient_chart_no, o.patient_phone,
                      o.operation_type, o.delete_reason].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (dateFrom && (o.deleted_at || "").slice(0, 10) < dateFrom) return false;
      if (dateTo && (o.deleted_at || "").slice(0, 10) > dateTo) return false;
      if (dayFilter !== "all" && (o.operation_day_date || o.date || "") !== dayFilter) return false;
      return true;
    });
  }, [items, search, dateFrom, dateTo, dayFilter]);

  const restore = async (op) => {
    if (!window.confirm(
      `Відновити операцію?\n\nПацієнт: ${op.patient_name}\nОперація: ${op.operation_type} · ${op.eye}\nДень: ${op.operation_day_date || op.date}\n\nПлатежі буде повернуто (voided → active), нарахування зарплати перераховано.`
    )) return;
    setRestoring((prev) => ({ ...prev, [op.id]: true }));
    try {
      await api.post(`/operations/${op.id}/restore`);
      toast.success("Операцію відновлено");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRestoring((prev) => { const n = { ...prev }; delete n[op.id]; return n; }); }
  };

  if (loading && !items.length) {
    return <div className="text-slate-500">Завантаження…</div>;
  }
  if (me && me.role !== "admin") {
    return (
      <div className="p-8 text-center space-y-2">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500" />
        <div className="text-lg font-semibold">Доступ обмежено</div>
        <div className="text-sm text-slate-500">Сторінку «Кошик» видно лише суперадміну.</div>
        <Button variant="outline" onClick={() => navigate("/")} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> На дашборд
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="deleted-ops-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027] flex items-center gap-2">
            <Trash2 className="h-7 w-7 text-red-500" /> Кошик (видалені операції)
          </h1>
          <p className="text-sm text-slate-500">
            {filtered.length} із {items.length} soft-deleted записів · доступ лише суперадміну
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/operations")} data-testid="bin-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> До журналу
        </Button>
      </div>

      {/* Filters */}
      <Card className="gv-card">
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              data-testid="bin-search"
              placeholder="Пошук: ПІБ, № картки, телефон, тип операції, причина видалення…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Видалено від</Label>
              <Input type="date" data-testid="bin-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Видалено до</Label>
              <Input type="date" data-testid="bin-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Операційний день</Label>
              <Select value={dayFilter} onValueChange={setDayFilter}>
                <SelectTrigger data-testid="bin-day-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Усі</SelectItem>
                  {uniqueDayDates.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="gv-card">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3">Видалено</th>
                <th className="text-left p-3">Ким</th>
                <th className="text-left p-3">Пацієнт</th>
                <th className="text-center p-3">Картка</th>
                <th className="text-left p-3">Операція</th>
                <th className="text-center p-3">Око</th>
                <th className="text-center p-3">День</th>
                <th className="text-right p-3">Сума</th>
                <th className="text-left p-3">Причина</th>
                <th className="text-right p-3 w-24">Дії</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((op) => (
                <tr key={op.id} data-testid={`bin-row-${op.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="p-3 text-xs text-slate-500">
                    {(op.deleted_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-3 text-xs text-slate-600">{op.deleted_by_name || users[op.deleted_by] || op.deleted_by || "—"}</td>
                  <td className="p-3 font-medium">{op.patient_name}</td>
                  <td className="p-3 text-center text-slate-600 tabular-nums">{op.patient_chart_no || "—"}</td>
                  <td className="p-3 text-slate-700">{op.operation_type}</td>
                  <td className="p-3 text-center">
                    <Badge className={op.eye === "OD" ? "bg-emerald-50 text-emerald-700" : op.eye === "OS" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}>
                      {op.eye}
                    </Badge>
                  </td>
                  <td className="p-3 text-center text-xs">{op.operation_day_date || op.date || "—"}</td>
                  <td className="p-3 text-right tabular-nums">{formatUAH(op.total_amount)}</td>
                  <td className="p-3 text-xs text-slate-700 max-w-xs">
                    <div className="truncate" title={op.delete_reason || ""}>
                      {op.delete_reason || <span className="text-slate-400">—</span>}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restore(op)}
                      disabled={!!restoring[op.id]}
                      data-testid={`bin-restore-${op.id}`}
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    >
                      <RotateCcw className={`h-3.5 w-3.5 mr-1 ${restoring[op.id] ? "animate-spin" : ""}`} />
                      Відновити
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-slate-400">
                    Кошик порожній — soft-deleted операцій не знайдено за поточними фільтрами
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
